/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-call */
// Above global eslint rules disabled as GlideJS has no TS implementation
/**
 * Carousel Component
 * @module patterns/components/carousels/carousel
 */
// eslint-disable-next-line @typescript-eslint/ban-tslint-comment
/* tslint:disable:no-any */

// Dependencies
import Glide from '@glidejs/glide';

import { markElementReady } from '@root/src/framework/scripts/helpers/element-ready';
import { scrollIntoView } from '~/framework/utils/scroll';
import { type App } from '~/app';

import { CLASS_NAME, OPTIONS } from './constants';

// eslint-disable-next-line import/no-unassigned-import
import './multi-image-carousel.scss';


export const carousels = new Map<string, Glide>();

export default (app: App) => {
    app.logger.debug('[Stockland FE] Initialise Stockland Multi Image Carousel Component');

    const listeners: Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]> = [];

    app.afterMount
        .then(() => setTimeout(() => {
            mountMultiImageCarousel(listeners)
        },200) )
        .catch((error: ErrorOptions) => {
            throw new Error('[Stockland FE] Unable to register Stockland Multi Image Carousel Component', error);
        });

    return () => {
        unMountMultiImageCarousel(listeners);

        app.logger.debug('[DXA] Horizontal Tab Bar Container Component destroyed');
    };
}

function unMountMultiImageCarousel(listeners: Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]>) {
    for (const [element, event, listener] of listeners) {
        element.removeEventListener(event, listener);
    }
}


export function mountMultiImageCarousel(listeners: Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]>) {
    const elements = document.querySelectorAll<HTMLElement>(`.${CLASS_NAME}`);

    // Loop through and individually instantiate each Glide instance
    const updateDisableButtons = (element: HTMLElement, index: number) => {
        const navButtons = element.querySelectorAll<HTMLButtonElement>('.article-button');
        const slides = element.querySelectorAll('.carousel__slide');
        if(!index) {
            for(const navButton of navButtons) {
                const button = navButton.querySelector('button')!;
                if(navButton.classList.contains('prev-article-button')) {
                    navButton.classList.add('disabled')
                    button.disabled = true;
                } else {
                    navButton.classList.remove('disabled')
                    button.disabled = false;
                }
            }
        } else if(index === slides.length - 1) {
            for(const navButton of navButtons) {
                const button = navButton.querySelector('button')!;
                if(navButton.classList.contains('next-article-button')) {
                    navButton.classList.add('disabled')
                    button.disabled = true;
                } else {
                    navButton.classList.remove('disabled')
                    button.disabled = false;
                }
            }
        } else {
            for(const navButton of navButtons) {
                navButton.classList.remove('disabled');
                const button = navButton.querySelector('button')!;
                button.disabled = false;
            }
        }
    }

    const initialiseMultiImageCarousel = (elements: NodeListOf<HTMLElement>) => {
        for (const [index, element] of elements.entries()) {

            const carouselId = element.dataset.carouselId ?? '';
            const indexClass = `${CLASS_NAME}-${carouselId}-${index}`;
            element.classList.add(indexClass);

            const existingGlides = carousels.get(indexClass) as Glide;
            if (existingGlides) {
                existingGlides.destroy();
            }
            
            //@ts-expect-error Types of property 'classes' are incompatible.
            const glide = new Glide(`.${indexClass}`, {
                ...OPTIONS,
                type: 'slider',
                perView: 1,
                gap:  32,
                autoplay: false,
                animationTimingFunc: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
                swipeThreshold: false,
                dragThreshold: false

            });

            // On each slide click get the slide index and set it active
            for (const slides of element.querySelectorAll('.multi-image-carousel__slides')) {
                slides.addEventListener('click', function (e) {
                    const slideNode = (e.target as HTMLElement).closest('.carousel__slide');
                    // eslint-disable-next-line unicorn/prefer-dom-node-dataset, unicorn/prefer-number-properties
                    const ind = Number(slideNode?.getAttribute('data-index'));
                    if (ind === 0 || ind > 0) {
                        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                        glide.go('=' + (ind - 1));
                    }
                });
            }

            // Scrolls active tab into view when slide is active
            glide.on(['run'], function () {
                const slideNumber: number = glide.index;
                const tabs = element.querySelectorAll<HTMLElement>('.nav-link');
                for (const tab of tabs) {
                    if(tab.dataset.glideDir == `=${slideNumber}`){
                        scrollIntoView(tab, {
                            behavior: 'smooth',
                            inline: 'center',
                            block: 'nearest'
                        });
                    }
                }

                updateDisableButtons(element, slideNumber);
            })

            glide.mount();
            updateDisableButtons(element, 0);

            carousels.set(indexClass, glide);

            markElementReady(element);

        }
    };

    initialiseMultiImageCarousel(elements);


    const destroyMultiImageCarousel = () => {
        // Destory each Glide instance to prevent memory leaks
        for (const [, glide] of carousels) {
            glide.destroy();
        }

        for (const [element, event, listener] of listeners) {
            element.removeEventListener(event, listener);
        }
    };

    return { elements, carousels, destroyMultiImageCarousel };
}
